import type { BadgeProps } from '@/components/ui';
import type { IqServiceInventoryStatus } from '@/types/iq-service-inventory';

// Re-export the opaque key pattern from the neutral types layer so existing imports
// (`from './iq-service-inventory-status'`) keep working; the single definition lives
// in @/types/iq-service-inventory (shared with the services feature, Phase 6).
export { IQ_SERVICE_KEY_PATTERN } from '@/types/iq-service-inventory';

/** Display/grouping order for the three inventory statuses. */
export const IQ_SERVICE_STATUS_ORDER: readonly IqServiceInventoryStatus[] = [
  'active',
  'pending_provisioning',
  'archived',
] as const;

/** Statuses offered when REGISTERING (UC-2). `archived` is never a create choice. */
export const IQ_SERVICE_CREATE_STATUS_OPTIONS: readonly IqServiceInventoryStatus[] = [
  'active',
  'pending_provisioning',
] as const;

/**
 * Allowed status transitions (Draft-Contract v0.9, UC-3). The edit form offers the
 * current status plus these targets — never a forbidden `→ pending_provisioning`.
 * Single place to re-sync when the authoritative Contract (Facade-Inc-1) lands.
 */
const IQ_SERVICE_STATUS_TRANSITIONS: Record<IqServiceInventoryStatus, readonly IqServiceInventoryStatus[]> = {
  active: ['archived'],
  pending_provisioning: ['active', 'archived'],
  archived: ['active'],
};

/** Selectable status values in the edit form = current status + its allowed targets. */
export function iqServiceEditStatusOptions(
  current: IqServiceInventoryStatus,
): IqServiceInventoryStatus[] {
  return [current, ...IQ_SERVICE_STATUS_TRANSITIONS[current]];
}

/** Badge variant per status (active = prominent, archived = muted). */
export function iqServiceStatusBadgeVariant(
  status: IqServiceInventoryStatus,
): NonNullable<BadgeProps['variant']> {
  switch (status) {
    case 'active':
      return 'secondary';
    case 'pending_provisioning':
      return 'default';
    case 'archived':
      return 'outline';
  }
}
